import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Flame, MessageCircle, Trophy } from "lucide-react";
import api from "@/lib/api";
import LucideIconByName from "@/components/LucideIconByName";

function timeAgo(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}d`;
  } catch { return ""; }
}

/** Compact 3-card horizontal rail of the hottest forum threads (last 48h). */
export default function HotInForums({ limit = 3 }) {
  const [threads, setThreads] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/forums_hot", { params: { limit, hours: 48 } })
      .then(({ data }) => { if (alive) setThreads(Array.isArray(data) ? data : []); })
      .catch(() => { if (alive) setThreads([]); });
    return () => { alive = false; };
  }, [limit]);

  if (!threads || threads.length === 0) return null;

  return (
    <section data-testid="hot-in-forums" className="mb-8">
      <div className="flex items-baseline gap-2 mb-3">
        <Flame className="w-4 h-4 text-[var(--tt-primary)]" />
        <h2 className="font-display font-bold text-base tracking-tight">Hot in Forums</h2>
        <span className="text-[10px] uppercase tracking-wider font-bold text-[var(--tt-text-faint)]">last 48h</span>
        <Link to="/forums" className="ml-auto text-[10px] uppercase tracking-wider font-bold text-[var(--tt-primary)] hover:text-[var(--tt-primary-strong)]">
          Browse all →
        </Link>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {threads.map((t) => (
          <Link
            key={t.thread_id}
            to={`/forums/${t.forum_slug}/${t.thread_id}`}
            data-testid={`hot-thread-${t.thread_id}`}
            className="group bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-xl p-4 hover:border-[var(--tt-primary)]/50 transition-colors flex flex-col"
            style={{ borderLeft: `3px solid ${t.forum_color}` }}
          >
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold mb-2"
                 style={{ color: t.forum_color }}>
              <LucideIconByName name={t.forum_icon} className="w-3 h-3" color={t.forum_color} strokeWidth={2.4} />
              {t.forum_name}
            </div>
            <h3 className="font-display font-bold text-sm leading-tight line-clamp-2 group-hover:text-[var(--tt-primary)] transition-colors">
              {t.title}
            </h3>
            <div className="mt-auto pt-3 flex items-center gap-3 text-[11px] text-[var(--tt-text-muted)]">
              <span className="font-mono-tt">by {t.user_name}</span>
              <span className="ml-auto flex items-center gap-3">
                <span className="flex items-center gap-1">
                  <MessageCircle className="w-3 h-3" /> {t.post_count}
                </span>
                {t.reactions_total > 0 && (
                  <span className="flex items-center gap-1 text-[var(--tt-yellow)]">
                    <Trophy className="w-3 h-3" /> {t.reactions_total}
                  </span>
                )}
                <span>{timeAgo(t.last_post_at)}</span>
              </span>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
